// Landingspagina voor het betaalde aanbod "4 weken coaching" (€19, geld terug als
// je na minstens 8 trainingen niet tevreden bent). Zelfde vormgeving als /online-coaching
// (Page.css daarvan), eigen tekst. Getoetst met 3 avatar-agents en een ads-expert
// (25-10-2026, zie ads/2026-10-start/ADS.md). Alles om te beslissen staat boven
// de vouw: bewijs, belofte, prijs, garantie, knop en "er loopt niets door".
//
// Betalen: e-mail invullen → floawch maakt een Stripe Checkout (iDEAL, €19
// eenmalig) en stuurt de bezoeker door. Na betalen gaat het in de app verder
// (account, intake, eerste trainingsdag).
import { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { initLpTracking, trackLp, applyNoIndex } from '../../utils/lpTracking';
import { suggestEmailFix } from '../../utils/emailTypo';
import '../OnlineCoaching/Page.css';

const API = 'https://hl-project.nl';
const STATS_URL = `${API}/api/public/landing-stats`;
const isDev = () => window.location.hostname.includes('localhost') || window.location.hostname.includes('ngrok');

const nfNL = new Intl.NumberFormat('nl-NL');
const fmtDaysAgo = (days) => (days <= 0 ? 'vandaag' : days === 1 ? 'gisteren' : `${days} dagen geleden`);
const fmtLastReviewed = (iso) => (iso ? fmtDaysAgo(Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)) : null);

const readCookie = (name) => {
  const m = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]+)'));
  return m ? decodeURIComponent(m[2]) : null;
};

// Echte reviews (zie OnlineCoaching/Page.js voor de bron)
const reviews = [
  { name: 'Art', source: 'via Facebook', text: 'Ik ben weer blij wanneer ik in de spiegel kijk. Zoveel geleerd waar ik echt wat aan heb en aan zal blijven hebben.' },
  { name: 'Alexander Visser', source: 'via Google', text: 'Sinds ik hulp van Stef krijg, merk ik dat mijn spieren groeien. Eerst op eigen kracht, maar dat ging met ups en downs.' },
  { name: 'Mike Peeters', source: 'via Google', text: 'Ik word eindelijk sterker, wat mij hiervoor nooit lukte.' }
];

const steps = [
  { title: 'Je betaalt €19 en maakt je account aan', text: 'Met iDEAL. Voor je account heb je alleen je e-mailadres nodig.' },
  { title: 'Je vertelt Stef wat je wilt', text: 'Een paar minuten: je doel, je niveau, je dagen, waar je meer op wilt focussen en of er iets is waar hij rekening mee moet houden. Je kiest meteen de dag van je eerste training.' },
  { title: 'Binnen 24 uur staat je schema in de app', text: 'Stef maakt het zelf, op jou. Hij appt je als het klaarstaat.' },
  { title: 'Na elke training de volgende klaar', text: 'De eerste keer kies je zelf je gewicht. Daarna zet Stef het klaar. 4 weken lang, en je kunt hem altijd appen.' }
];

const faq = [
  { q: 'Loopt er na 4 weken iets door?', a: 'Nee. Je betaalt eenmalig €19. Wil je daarna door, dan kies je dat zelf in de app: €39 per maand, elke maand opzegbaar, en je €19 gaat van de eerste maand af.' },
  { q: 'Hoe krijg ik mijn geld terug?', a: 'App Stef binnen 7 dagen na je 4 weken dat je je geld terug wilt. Je krijgt het binnen een paar dagen op je rekening. Je hoeft niet uit te leggen waarom.' },
  { q: 'Is het schema op maat?', a: 'Ja. Stef maakt het zelf, op jouw doel, niveau, dagen en wensen. Na elke training stuurt hij het bij op wat je doet. Ga je na 4 weken door, dan maakt hij je volgende blok op basis van je eerste 4 weken.' },
  { q: 'Wat kijkt Stef precies na?', a: 'Wat je invult: gewicht, herhalingen en hoe zwaar het voelde. Daarop zet hij je volgende training klaar. Hoe een oefening moet, zie je in de video bij elke oefening. Naar je techniek kijkt hij niet.' },
  { q: 'Ik train al jaren. Heb ik hier wat aan?', a: 'Als je al een tijd traint, gaat het meestal niet meer vanzelf vooruit. Dan helpt het dat iemand na elke training kijkt waar nog een stap in zit.' },
  { q: 'Ik heb een blessure of ben wat ouder.', a: 'Geef het aan in de intake of app Stef. Hij houdt er bij je schema rekening mee.' },
  { q: 'Hoeveel tijd kost het?', a: 'Net zoveel als nu: je traint 3 of 4 keer per week. In de intake geef je aan hoeveel tijd je per training hebt.' }
];

// Eén formulier: e-mail → naar de betaalpagina van Stripe (iDEAL)
function StartForm({ location }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [suggestion, setSuggestion] = useState(null);
  const [ignoredTypoFor, setIgnoredTypoFor] = useState(null);

  const go = async (address) => {
    setSuggestion(null);
    setError('');
    setBusy(true);
    trackLp('checkout_start', { location });
    // "Betaling gestart": hierop optimaliseert de campagne de eerste weken
    if (window.fbq && !isDev()) window.fbq('track', 'InitiateCheckout', { value: 19, currency: 'EUR', content_name: 'start19' });
    try {
      const params = new URLSearchParams(window.location.search);
      const res = await fetch(`${API}/api/public/start-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: address,
          utm: Object.fromEntries([...params.entries()].filter(([k]) => k.startsWith('utm_'))),
          fbclid: params.get('fbclid') || null,
          fbp: readCookie('_fbp'),
          fbc: readCookie('_fbc'),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.url) throw new Error(data.message || 'checkout');
      window.location.href = data.url;
    } catch (err) {
      setBusy(false);
      setError('Dat lukte niet. Probeer het nog een keer, of app Stef op +31 6 27 00 30 89.');
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const fix = suggestEmailFix(email);
    if (fix && ignoredTypoFor !== email) { setSuggestion(fix); return; }
    go(email);
  };

  return (
    <>
      {suggestion && (
        <div className="oc-typo" role="alert">
          <p>Bedoel je <strong>{suggestion}</strong>?</p>
          <div className="oc-typo-actions">
            <button type="button" className="oc-typo-yes" onClick={() => { setEmail(suggestion); go(suggestion); }}>Ja, verbeter</button>
            <button type="button" className="oc-typo-no" onClick={() => { setIgnoredTypoFor(email); go(email); }}>Nee, het klopt zo</button>
          </div>
        </div>
      )}
      <form className="oc-form" onSubmit={onSubmit}>
        <input
          type="email" name="email" placeholder="Jouw e-mailadres" value={email} required
          autoComplete="email" inputMode="email" autoCapitalize="off" autoCorrect="off" spellCheck="false" enterKeyHint="go"
          onChange={(e) => { setEmail(e.target.value); if (suggestion) setSuggestion(null); }}
        />
        <button type="submit" disabled={busy}>{busy ? 'Even geduld...' : 'Start voor €19'}</button>
      </form>
      {error && <p className="oc-microcopy" style={{ color: '#ff6b76' }}>{error}</p>}
    </>
  );
}

function StartOffer() {
  useEffect(() => {
    const prev = document.title;
    document.title = '4 weken coaching voor €19 | STRONGR by Stef';
    return () => { document.title = prev; };
  }, []);

  useEffect(() => {
    if (window.fbq && !isDev()) window.fbq('track', 'PageView');
    const cleanupNoIndex = applyNoIndex();
    const cleanupTracking = initLpTracking('start19');
    return () => { cleanupTracking(); cleanupNoIndex(); };
  }, []);

  const [stats, setStats] = useState(null);
  useEffect(() => {
    fetch(STATS_URL).then((r) => (r.ok ? r.json() : null)).then((d) => { if (d?.totals) setStats(d); }).catch(() => {});
  }, []);

  const [openFaq, setOpenFaq] = useState(null);
  const [showSticky, setShowSticky] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const input = document.querySelector('.oc-hero input[type="email"]');
    if (input) setTimeout(() => input.focus(), 650);
  };

  return (
    <div className="oc-page">
      <div className="oc-topbar">
        <img src="/img/strongr-by-stef-logo-transparent.svg" alt="STRONGR by Stef" style={{ height: 44 }} />
        <span style={{ color: '#b5ada6', fontSize: 14 }}><span style={{ color: '#ffb800' }}>★★★★★</span>&nbsp; 5.0 op Google</span>
      </div>

      {/* HERO: bewijs, belofte, prijs, garantie, knop */}
      <section className="oc-hero">
        <div className="oc-hero-inner">
          <div className="oc-hero-stars"><span className="oc-stars">★★★★★</span> 5.0 op Google · 36+ reviews</div>
          <h1>3 of 4 keer per week in de sportschool. <span className="oc-red">Laat het ook wat opleveren.</span></h1>
          <p className="oc-sub">
            Stef maakt je schema op maat, binnen 24 uur. Daarna zet hij elke training voor je klaar: gewicht, herhalingen en rust.
            Na elke training kijkt hij wat je hebt ingevuld en zet hij de volgende klaar.
          </p>
          <p className="oc-offer-line">4 weken coaching voor €19. Train minstens 8 keer, niet tevreden? Geld terug.</p>
          <StartForm location="hero" />
          <p className="oc-microcopy">
            <span>Eenmalig met iDEAL</span> · <span>Er loopt niets automatisch door</span>
          </p>
        </div>

        {stats && (
          <div className="oc-proofbar">
            <div className="oc-proofbar-item"><strong>{nfNL.format(stats.totals.sessionsLogged)}</strong><span>trainingen nagekeken door Stef</span></div>
            {stats.progression?.medianPct ? (
              <div className="oc-proofbar-item"><strong>+{stats.progression.medianPct}%</strong><span>meer gewicht, mediaan van klanten</span></div>
            ) : null}
            {fmtLastReviewed(stats.lastReviewedAt) && (
              <div className="oc-proofbar-item">
                <strong className="oc-proofbar-live"><span className="oc-live-dot" />{fmtLastReviewed(stats.lastReviewedAt)}</strong>
                <span>laatste training nagekeken</span>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Zo ziet je training eruit */}
      <section className="oc-section">
        <div className="oc-section-inner">
          <p className="oc-kicker">In de app</p>
          <h2 className="oc-h2">Zo ziet je training eruit</h2>
          <img src="/img/app/ui-app-sessie-lp.jpg" alt="Een training in de app: per oefening gewicht, sets, herhalingen en rust" loading="lazy"
            style={{ width: '100%', maxWidth: 420, borderRadius: 16, display: 'block', margin: '16px auto' }} />
          <p className="oc-lead">Ging het makkelijk, dan gaat er de volgende keer wat bij. Was het zwaar, dan blijft het staan. Zo kom je elke week een stap verder.</p>
        </div>
      </section>

      {/* Wie is Stef */}
      <section className="oc-section">
        <div className="oc-section-inner">
          <p className="oc-kicker">Je coach</p>
          <h2 className="oc-h2">Wie is Stef</h2>
          <img src="/img/stef-schrijven-lp.jpg" alt="Stef" loading="lazy" style={{ width: '100%', maxWidth: 420, borderRadius: 16, display: 'block', margin: '16px auto' }} />
          {/* TODO(Stef): 2-3 zinnen over zichzelf (hoe lang coach, opleiding, waar PT) en een foto in de sportschool */}
          <p className="oc-lead">Ik ben Stef, personal trainer. Ik maak elk schema zelf en kijk elke training na die je in de app invult. Wat je de volgende keer doet, beslis ik.</p>
        </div>
      </section>

      {/* Zo gaat het */}
      <section className="oc-section">
        <div className="oc-section-inner">
          <h2 className="oc-h2">Zo gaat het</h2>
          <div className="oc-steps">
            {steps.map((s, i) => (
              <div key={s.title} className="oc-card oc-step">
                <span className="oc-step-num">{i + 1}</span>
                <h3 style={{ fontWeight: 700, fontSize: 18, marginBottom: 10 }}>{s.title}</h3>
                <p style={{ color: '#c9c2bc', fontSize: 15, lineHeight: 1.55 }}>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Garantie en na 4 weken */}
      <section className="oc-section">
        <div className="oc-section-inner">
          <div className="oc-offer-card">
            <div className="oc-offer-left">
              <p className="oc-kicker">Garantie</p>
              <h2 className="oc-h2">Train minstens 8 keer. Niet tevreden? <span className="oc-red">Geld terug.</span></h2>
              <p className="oc-lead">Doe in de 4 weken minstens 8 trainingen met de app. Vind je het niks, app Stef binnen 7 dagen na je 4 weken en je krijgt je €19 terug. Je hoeft niet uit te leggen waarom.</p>
            </div>
            <div className="oc-offer-right">
              <p className="oc-kicker">En na 4 weken?</p>
              <p className="oc-lead">Dan kent Stef je. Hij weet hoe je traint, wat je aankunt en waar je sterk en minder sterk in bent. Ga je door, dan maakt hij je volgende blok helemaal op jou, op basis van je eerste 4 weken.</p>
              <p className="oc-microcopy">€39 per maand, elke maand opzegbaar. Je €19 gaat van de eerste maand af. Wil je niet door, dan hoef je niets te doen.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="oc-section">
        <div className="oc-section-inner">
          <p className="oc-kicker">36+ vijfsterren-reviews</p>
          <h2 className="oc-h2">Wat klanten zeggen</h2>
          <div className="oc-reviews">
            {reviews.map((r) => (
              <div key={r.name} className="oc-card">
                <div className="oc-review-stars">★★★★★</div>
                <p className="oc-review-text">"{r.text}"</p>
                <div className="oc-review-author">{r.name}, {r.source}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="oc-section">
        <div className="oc-section-inner">
          <h2 className="oc-h2">Vragen</h2>
          {faq.map((f, i) => (
            <div key={f.q} className="oc-faq-item">
              <button type="button" onClick={() => { if (openFaq !== i) trackLp('faq_open', { value: i, meta: { q: f.q } }); setOpenFaq(openFaq === i ? null : i); }}>
                <span>{f.q}</span>
                <ChevronDown size={20} style={{ flexShrink: 0, transition: 'transform .25s ease', transform: openFaq === i ? 'rotate(180deg)' : 'none' }} />
              </button>
              {openFaq === i && <p>{f.a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* Slot */}
      <section className="oc-final">
        <div style={{ position: 'relative', maxWidth: 680, margin: '0 auto' }}>
          <h2 className="oc-h2">Begin deze week</h2>
          <p className="oc-lead">4 weken coaching voor €19. Train minstens 8 keer, niet tevreden? Geld terug.</p>
          <StartForm location="final" />
          <p className="oc-microcopy"><span>Eenmalig met iDEAL</span> · <span>Er loopt niets automatisch door</span></p>
        </div>
      </section>

      <footer style={{ borderTop: '1px solid rgba(255,255,255,.08)', padding: '30px 20px 100px', background: '#100d0b', color: '#6d665f', fontSize: 13, textAlign: 'center' }}>
        <p>© Healthy Living Project {new Date().getFullYear()} · Vragen? info@healthyliving-project.com of app Stef op +31 6 27 00 30 89</p>
        <p style={{ marginTop: 6 }}>Resultaten verschillen per persoon en vragen consistente training.</p>
      </footer>

      {showSticky && (
        <div className="oc-sticky">
          <button type="button" onClick={() => { trackLp('cta_click', { location: 'sticky' }); toTop(); }}
            style={{ width: '100%', border: 'none', borderRadius: 12, padding: '15px 0', background: 'linear-gradient(180deg, #e51423, #b40a16)', color: '#fff', fontWeight: 700, fontSize: 16, fontFamily: 'inherit', cursor: 'pointer' }}>
            Start voor €19 · geld terug als het niks is
          </button>
        </div>
      )}
    </div>
  );
}

export default StartOffer;
